// Apex Business Intelligence — Round 11 Restoration-Industry Modules (additive)
//
// Components exported:
//   - shouldRenderR11Public, R11PublicApp  (handles /#/r11/sign/:token and /#/r11/survey/:token)
//   - RestorationOverlay                   (the all-in-one job overlay with 5 tabs:
//                                            Survey · Compliance · Moisture · Photos · Insurance · Close)
//   - RestorationReportsPage               (the new Reports menu item)
//   - ScheduleAllViewsPage                 (month/week/tech/emp/emp-week toggles + Day)
//   - MapPinsContacts                      (Call + SMS buttons in pin drawer)
//   - TeamRowExtras                        (Reset Password + View Activity Log helpers)
//   - PinPrintSendButtons                  (drop-in toolbar for the restoration reports)
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { DryReadingsTab, RepairsTab, SketchTab } from "./round14";
import { toast } from "./intelligence";

// R15 — small inline styles used by PhotosTabR14
const _inDark = { padding: "6px 8px", border: "1px solid #c8c8c8", borderRadius: 4, fontSize: 11, color: "#1a1a1a", fontWeight: 500, background: "#fff" };
const _delBtn = { background: "transparent", border: "1px solid #C0392B", color: "#C0392B", borderRadius: 3, padding: "2px 6px", fontSize: 11, cursor: "pointer", fontWeight: 700 };

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "apex_auth_token";

const _h = () => { const t = localStorage.getItem(TOKEN_KEY); return t ? { Authorization: "Bearer " + t } : {}; };
const j = (path, opts = {}) =>
  fetch(`${API}${path}`, { ...opts, headers: { "Content-Type": "application/json", ..._h(), ...(opts.headers || {}) } })
    .then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw Object.assign(new Error(d.detail || r.statusText), { data: d, status: r.status }); return d; });

const card = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: 14, marginBottom: 12, color: "#1a1a1a" };
const subhead = { fontFamily: "Rajdhani,sans-serif", fontSize: 13, fontWeight: 700, marginBottom: 8, letterSpacing: 1, color: "#1a1a1a", textTransform: "uppercase" };
const btn = { padding: "7px 12px", border: "none", background: "#dc2626", color: "#fff", fontSize: 12, fontWeight: 600, borderRadius: 4, cursor: "pointer", letterSpacing: 0.5 };
const btnAlt = { ...btn, background: "#f8fafc", color: "#1a1a1a" };
const btnGhost = { ...btn, background: "transparent", border: "1px solid #C0C0C0", color: "#1a1a1a" };
// R13d — global input/label override so text is readable on light card bg
const fldStyle = { color: "#1a1a1a", background: "#fff" };
const lblStyle = { color: "#1a1a1a", fontWeight: 600 };
const pill = (bg, fg) => ({ display: "inline-block", padding: "2px 8px", borderRadius: 999, fontSize: 10, background: bg, color: fg || "#fff", fontWeight: 600, letterSpacing: 0.5 });

// ════════════════════════════════════════════════════════════════════════════
// PUBLIC HASH ROUTE — signing + survey
// ════════════════════════════════════════════════════════════════════════════
export function shouldRenderR11Public() {
  const h = (typeof window !== "undefined" ? window.location.hash : "") || "";
  return /^#\/r11\/(sign|survey)\//.test(h);
}

export function R11PublicApp() {
  const h = window.location.hash;
  const signMatch = h.match(/^#\/r11\/sign\/([a-f0-9]+)/i);
  const surveyMatch = h.match(/^#\/r11\/survey\/([a-f0-9]+)/i);
  if (signMatch) return <PublicSignPage token={signMatch[1]} />;
  if (surveyMatch) return <PublicSurveyPage token={surveyMatch[1]} />;
  return <div style={{ padding: 40, textAlign: "center" }}>Link not recognized.</div>;
}

function PublicSignPage({ token }) {
  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState(null);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  useEffect(() => {
    fetch(`${API}/api/iv/r11/sign/lookup/${token}`).then(r => r.json())
      .then(d => { if (d.ok === false) setErr(d.message || d.status); else setMeta(d); })
      .catch(e => setErr(e.message));
  }, [token]);
  if (err) return <div style={{ padding: 30, textAlign: "center", maxWidth: 500, margin: "auto" }}><h2>⚠️ {err}</h2></div>;
  if (!meta) return <div style={{ padding: 30, textAlign: "center" }}>Loading…</div>;
  if (meta.status === "signed") return (
    <div style={{ padding: 30, textAlign: "center", maxWidth: 500, margin: "auto" }} data-testid="r11-sign-already">
      <h2 style={{ color: "#16a34a" }}>✓ Already signed</h2>
      <p>This {meta.doc_type_label} was signed by {meta.signed_by} on {meta.signed_at}.</p>
    </div>
  );
  if (done) return (
    <div style={{ padding: 30, textAlign: "center", maxWidth: 500, margin: "auto" }} data-testid="r11-sign-done">
      <h2 style={{ color: "#16a34a" }}>✓ Signature recorded</h2>
      <p>Thank you {typed}. A copy has been sent to you. You may close this window.</p>
    </div>
  );
  const submit = async (decline = false) => {
    if (!decline && typed.trim().length < 2) { alert("Type your full name to confirm"); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/iv/r11/sign/submit/${token}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ typed_name: typed.trim(), decline }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || r.statusText);
      setDone(true);
    } catch (e) { alert("Failed: " + e.message); } finally { setBusy(false); }
  };
  return (
    <div style={{ maxWidth: 640, margin: "0 auto", padding: 18, fontFamily: "Inter,system-ui,sans-serif" }} data-testid="r11-sign-page">
      <div style={{ borderBottom: "3px solid #dc2626", paddingBottom: 10, marginBottom: 16 }}>
        <div style={{ fontFamily: "Rajdhani,sans-serif", fontSize: 22, fontWeight: 800, letterSpacing: 1 }}>APEX INFINITY VENTURES</div>
        <div style={{ color: "#475569", fontSize: 13 }}>{meta.doc_type_label}</div>
      </div>
      <div style={{ background: "#f8fafc", padding: 14, borderRadius: 6, marginBottom: 14, fontSize: 13 }}>
        <div><strong>Customer:</strong> {meta.customer_name}</div>
        <div><strong>Property:</strong> {meta.property_address || "—"}</div>
        <div><strong>Job #:</strong> {meta.job_id}</div>
        {meta.scope_of_work && <div style={{ marginTop: 6 }}><strong>Scope:</strong><div style={{ whiteSpace: "pre-wrap" }}>{meta.scope_of_work}</div></div>}
      </div>
      <p style={{ fontSize: 13, color: "#111" }}>By typing your full name and tapping <strong>Submit Signature</strong> below, you legally acknowledge and accept the document above. Your signature, timestamp, IP address, and device will be recorded.</p>
      <div style={{ background: "#fff", border: "2px dashed #cbd5e1", borderRadius: 6, padding: 18, margin: "14px 0" }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 6 }}>Type your full name to confirm</label>
        <input data-testid="r11-sign-typed-name" type="text" value={typed} onChange={e => setTyped(e.target.value)}
          placeholder="Jane Q. Customer"
          style={{ width: "100%", fontSize: 24, padding: 10, fontFamily: "cursive", borderTop: "none", borderLeft: "none", borderRight: "none", borderBottom: "2px solid #1e293b", outline: "none" }} />
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        <button data-testid="r11-sign-submit" onClick={() => submit(false)} disabled={busy}
          style={{ ...btn, flex: 1, fontSize: 14, padding: "12px 16px" }}>{busy ? "Submitting…" : "Submit Signature"}</button>
        <button data-testid="r11-sign-decline" onClick={() => submit(true)} disabled={busy}
          style={{ ...btnGhost, fontSize: 12 }}>Decline</button>
      </div>
      <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 14, textAlign: "center" }}>Expires: {meta.expires_at?.slice(0, 19).replace("T", " ")}</div>
    </div>
  );
}

function PublicSurveyPage({ token }) {
  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState(null);
  const [ans, setAns] = useState({ overall_rating: 8, arrived_on_time: "yes", team_professional: "yes",
    work_satisfactory: "yes", site_left_clean: "yes", would_recommend: "yes", improvement: "", other_comments: "" });
  const [done, setDone] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/iv/r11/survey/lookup/${token}`).then(r => r.json())
      .then(d => { if (d.thanks) setDone({ thanks_only: true }); else setMeta(d); })
      .catch(e => setErr(e.message));
  }, [token]);
  if (err) return <div style={{ padding: 30, textAlign: "center" }}><h2>⚠️ {err}</h2></div>;
  if (!meta && !done) return <div style={{ padding: 30, textAlign: "center" }}>Loading…</div>;
  if (done) return (
    <div style={{ padding: 30, textAlign: "center", maxWidth: 500, margin: "auto" }} data-testid="r11-survey-done">
      <h2 style={{ color: "#16a34a" }}>✓ Thank you!</h2>
      <p>{done.thanks_only ? "Your response has been recorded." : "We appreciate your feedback."}</p>
      {done.google_prompt && (
        <div style={{ marginTop: 18, padding: 14, background: "#dcfce7", borderRadius: 6 }} data-testid="r11-survey-google">
          <p style={{ marginBottom: 8 }}>Would you mind leaving us a Google review?</p>
          <a data-testid="r11-survey-google-link" href="https://g.page/r/apex-infinity/review" target="_blank" rel="noreferrer"
             style={{ ...btn, textDecoration: "none", display: "inline-block" }}>Leave a Google Review</a>
        </div>
      )}
    </div>
  );
  const submit = async () => {
    try {
      const r = await fetch(`${API}/api/iv/r11/survey/submit/${token}`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(ans),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || r.statusText);
      setDone(d);
    } catch (e) { alert("Failed: " + e.message); }
  };
  const radio = (key, val) => (
    <label key={val} style={{ marginRight: 12, fontSize: 13 }}>
      <input type="radio" checked={ans[key] === val} onChange={() => setAns({ ...ans, [key]: val })}
        data-testid={`r11-survey-${key}-${val}`} /> {val}
    </label>
  );
  return (
    <div style={{ maxWidth: 600, margin: "0 auto", padding: 18, fontFamily: "Inter,system-ui,sans-serif" }} data-testid="r11-survey-page">
      <h2 style={{ fontFamily: "Rajdhani,sans-serif", letterSpacing: 1 }}>Quick Survey · Apex Infinity</h2>
      <p style={{ fontSize: 13, color: "#475569" }}>Hi {meta.customer_name || "there"} — takes under a minute. Thanks!</p>

      <div style={card}>
        <div style={{ fontSize: 13, marginBottom: 6 }}>Overall rating (1-10): <strong style={{ fontSize: 18 }}>{ans.overall_rating}</strong></div>
        <input data-testid="r11-survey-overall" type="range" min="1" max="10" value={ans.overall_rating}
          onChange={e => setAns({ ...ans, overall_rating: parseInt(e.target.value) })} style={{ width: "100%" }} />
      </div>

      {[
        ["arrived_on_time", "Did the team arrive on time?", ["yes", "somewhat", "no"]],
        ["team_professional", "Was the team professional?", ["yes", "somewhat", "no"]],
        ["work_satisfactory", "Was the work satisfactory?", ["yes", "partially", "no"]],
        ["site_left_clean", "Was the site left clean?", ["yes", "partially", "no"]],
        ["would_recommend", "Would you recommend us?", ["yes", "maybe", "no"]],
      ].map(([key, label, opts]) => (
        <div key={key} style={card}>
          <div style={{ fontSize: 13, marginBottom: 8, fontWeight: 600 }}>{label}</div>
          <div>{opts.map(o => radio(key, o))}</div>
        </div>
      ))}

      <div style={card}>
        <div style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>What could we improve?</div>
        <textarea data-testid="r11-survey-improvement" rows={3} value={ans.improvement}
          onChange={e => setAns({ ...ans, improvement: e.target.value })}
          style={{ width: "100%", padding: 6, border: "1px solid #C0C0C0", borderRadius: 4, fontSize: 13 }} />
      </div>
      <div style={card}>
        <div style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Other comments</div>
        <textarea data-testid="r11-survey-other" rows={2} value={ans.other_comments}
          onChange={e => setAns({ ...ans, other_comments: e.target.value })}
          style={{ width: "100%", padding: 6, border: "1px solid #C0C0C0", borderRadius: 4, fontSize: 13 }} />
      </div>
      <button data-testid="r11-survey-submit" onClick={submit} style={{ ...btn, width: "100%", padding: "12px 16px", fontSize: 14 }}>Submit Survey</button>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// RESTORATION OVERLAY — all-in-one job overlay
// ════════════════════════════════════════════════════════════════════════════
export function RestorationOverlay({ job, onClose }) {
  const [tab, setTab] = useState("survey");
  const jobId = job?.id || job?.job_number || job;
  const tabs = [
    ["survey", "Survey"],
    ["compliance", "Compliance"],
    ["dry", "Dry Readings"],
    ["repairs", "Repairs"],
    ["sketch", "Sketch"],
    ["moisture", "Moisture Map"],
    ["photos", "Photos"],
    ["claim", "Insurance Claim"],
    ["sign", "Signatures"],
    ["close", "Close Job"],
  ];
  return (
    <div className="modal-overlay" onClick={onClose} style={{ alignItems: "flex-start", paddingTop: 30 }}>
      <style>{`
        [data-testid="r11-overlay"] input,
        [data-testid="r11-overlay"] select,
        [data-testid="r11-overlay"] textarea,
        [data-testid="r11-overlay"] label,
        [data-testid="r11-overlay"] td,
        [data-testid="r11-overlay"] th,
        [data-testid="r11-overlay"] p,
        [data-testid="r11-overlay"] span,
        [data-testid="r11-overlay"] div { color: #1a1a1a; }
        [data-testid="r11-overlay"] input::placeholder,
        [data-testid="r11-overlay"] textarea::placeholder { color: #666; }
      `}</style>
      <div className="modal" data-testid="r11-overlay" onClick={e => e.stopPropagation()}
        style={{ maxWidth: 980, width: "calc(100vw - 40px)", maxHeight: "calc(100vh - 60px)", overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <div className="modal-header" style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", background: "#f5f5f5", borderBottom: "1px solid #d8d8d8" }}>
          <button onClick={onClose} data-testid="r11-back" style={{ ...btnGhost, fontSize: 11, color: "#2C2C2C", fontWeight: 600 }}>← Back</button>
          <div style={{ fontFamily: "Rajdhani,sans-serif", fontSize: 18, fontWeight: 800, letterSpacing: 1, color: "#2C2C2C" }}>Job Files · {jobId}</div>
          <div style={{ flex: 1, display: "flex", gap: 4, flexWrap: "wrap" }}>
            {tabs.map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`r11-tab-${k}`}
                style={{ ...btnGhost, padding: "4px 10px", fontSize: 11, fontWeight: 600,
                  background: tab === k ? "#1e40af" : "transparent",
                  color: tab === k ? "#fff" : "#2C2C2C",
                  borderColor: tab === k ? "#1e40af" : "#d1d5db" }}>{l}</button>
            ))}
          </div>
          <button className="icon-btn" onClick={onClose} style={{ color: "#2C2C2C" }}>×</button>
        </div>
        <div style={{ padding: 18, overflowY: "auto" }}>
          {tab === "survey" && <SurveyTab jobId={jobId} job={job} />}
          {tab === "compliance" && <ComplianceTab jobId={jobId} />}
          {tab === "dry" && <DryReadingsTab jobId={jobId} job={job} />}
          {tab === "repairs" && <RepairsTab jobId={jobId} />}
          {tab === "sketch" && <SketchTab jobId={jobId} job={job} />}
          {tab === "moisture" && <MoistureTab jobId={jobId} />}
          {tab === "photos" && <PhotosTabR14 jobId={jobId} />}
          {tab === "claim" && <InsuranceClaimTab jobId={jobId} />}
          {tab === "sign" && <SignaturesTab jobId={jobId} job={job} />}
          {tab === "close" && <CloseJobTab jobId={jobId} job={job} onClosed={onClose} />}
        </div>
      </div>
    </div>
  );
}

function SurveyTab({ jobId, job }) {
  const [list, setList] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => j(`/api/iv/r11/surveys?job_id=${jobId}`).then(setList).catch(() => setList({ surveys: [] })), [jobId]);
  useEffect(() => { load(); }, [load]);
  const sendNow = async () => {
    setBusy(true);
    try {
      const r = await j(`/api/iv/r11/survey/send/${jobId}`, { method: "POST", body: JSON.stringify({
        customer_name: job?.customer || "", customer_phone: job?.phone || job?.contact_phone || "",
        customer_email: job?.email || job?.contact_email || "",
      })});
      alert(`Survey sent. Link:\n${r.link}\nSMS: ${r.sms}  Email: ${r.email}`);
      load();
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  };
  if (!list) return <div>Loading…</div>;
  return (
    <div data-testid="r11-survey-tab">
      <div style={card}>
        <div style={subhead}>Customer Surveys</div>
        <div style={{ fontSize: 12, color: "#475569", marginBottom: 10 }}>
          Surveys auto-send when a job is closed. You can also send one manually.
        </div>
        <button data-testid="r11-survey-send-now" onClick={sendNow} disabled={busy} style={btnAlt}>
          {busy ? "Sending…" : "Send Survey Now"}
        </button>
      </div>
      <div style={card}>
        <div style={subhead}>Responses ({list.submitted_count}/{list.count})</div>
        {list.surveys.length === 0 && <div style={{ fontSize: 12, color: "#94a3b8" }}>No surveys yet for this job.</div>}
        {list.surveys.map(s => (
          <div key={s.id} data-testid={`r11-survey-row-${s.id}`} style={{ borderBottom: "1px solid #f1f5f9", padding: "8px 0", fontSize: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <strong>{s.customer_name || "—"}</strong>
              <span>{s.status === "submitted"
                ? <span style={pill(s.is_promoter ? "#16a34a" : s.is_detractor ? "#dc2626" : "#eab308")}>
                    {s.nps_score}/10 · {s.is_promoter ? "Promoter" : s.is_detractor ? "Detractor" : "Passive"}
                  </span>
                : <span style={pill("#6b7280")}>{s.status}</span>}
              </span>
            </div>
            <div style={{ color: "#475569" }}>Sent: {(s.created_at || "").slice(0, 16).replace("T", " ")}</div>
            {s.answers && (
              <div style={{ background: "#f8fafc", padding: 8, marginTop: 6, borderRadius: 4, fontSize: 11 }}>
                Recommend: <strong>{s.answers.would_recommend}</strong> · On time: {s.answers.arrived_on_time} ·
                Professional: {s.answers.team_professional} · Clean: {s.answers.site_left_clean}
                {s.answers.improvement && <div style={{ marginTop: 4 }}><em>Improve:</em> {s.answers.improvement}</div>}
                {s.answers.other_comments && <div><em>Comments:</em> {s.answers.other_comments}</div>}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ComplianceTab({ jobId }) {
  const [data, setData] = useState(null);
  const [tplKey, setTplKey] = useState("water_mitigation");
  const load = useCallback(() => j(`/api/iv/r11/compliance/${jobId}`).then(setData).catch(() => setData({ tasks: [], complete: 0, total: 0, overdue: 0 })), [jobId]);
  useEffect(() => { load(); }, [load]);
  const seed = async () => {
    await j(`/api/iv/r11/compliance/seed/${jobId}/${tplKey}`, { method: "POST" });
    load();
  };
  const updateTask = async (t, patch) => {
    await j(`/api/iv/r11/compliance/task`, { method: "PUT", body: JSON.stringify({ ...t, ...patch }) });
    load();
  };
  const grouped = useMemo(() => {
    const g = {};
    ((data && data.tasks) || []).forEach(t => { (g[t.category] = g[t.category] || []).push(t); });
    return g;
  }, [data]);
  if (!data) return <div>Loading…</div>;
  const pct = data.total ? Math.round(data.complete / data.total * 100) : 0;
  const today = new Date().toISOString().slice(0, 10);
  const inSevenDays = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
  const colorFor = (t) => {
    if (t.status === "Complete") return "#dcfce7";
    if (t.status === "In Progress") return "#dbeafe";
    if (t.due_date && t.due_date < today) return "#fee2e2";
    if (t.due_date && t.due_date === today) return "#ffedd5";
    if (t.due_date && t.due_date <= inSevenDays) return "#fef3c7";
    return "#f8fafc";
  };
  return (
    <div data-testid="r11-compliance-tab">
      <div style={card}>
        <div style={subhead}>Progress</div>
        <div style={{ background: "#f8fafc", borderRadius: 999, height: 14, overflow: "hidden" }}>
          <div data-testid="r11-compliance-progress" style={{ background: "#16a34a", height: "100%", width: `${pct}%`, transition: "width 0.4s" }} />
        </div>
        <div style={{ fontSize: 12, marginTop: 6 }}>{data.complete} of {data.total} complete ({pct}%) · {data.overdue} overdue</div>
      </div>
      {data.total === 0 && (
        <div style={card}>
          <div style={subhead}>Seed from template</div>
          <select value={tplKey} onChange={e => setTplKey(e.target.value)} data-testid="r11-compliance-tpl"
            style={{ padding: 6, fontSize: 12, marginRight: 8 }}>
            <option value="water_mitigation">Water Mitigation</option>
            <option value="mold_remediation">Mold Remediation</option>
            <option value="fire_smoke">Fire & Smoke</option>
            <option value="cat_response">CAT Response</option>
          </select>
          <button data-testid="r11-compliance-seed" onClick={seed} style={btn}>Load template</button>
        </div>
      )}
      {Object.entries(grouped).map(([cat, tasks]) => (
        <div key={cat} style={card}>
          <div style={subhead}>{cat}</div>
          {tasks.map(t => (
            <div key={t.id} data-testid={`r11-task-${t.id}`}
              style={{ background: colorFor(t), padding: 10, borderRadius: 4, marginBottom: 6, display: "flex", alignItems: "center", gap: 8 }}>
              <input type="checkbox" data-testid={`r11-task-check-${t.id}`}
                checked={t.status === "Complete"}
                onChange={e => updateTask(t, { status: e.target.checked ? "Complete" : "Not Started" })} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: t.required ? 600 : 400 }}>
                  {t.name} {t.required && <span style={pill("#dc2626")}>REQ</span>}
                </div>
                {t.completed_at && <div style={{ fontSize: 10, color: "#475569" }}>✓ {t.completed_by} · {t.completed_at.slice(0, 16).replace("T", " ")}</div>}
              </div>
              <select value={t.status} onChange={e => updateTask(t, { status: e.target.value })}
                data-testid={`r11-task-status-${t.id}`} style={{ fontSize: 11, padding: 3 }}>
                {["Not Started", "In Progress", "Complete", "Skipped"].map(s => <option key={s}>{s}</option>)}
              </select>
              <input type="date" value={t.due_date || ""} onChange={e => updateTask(t, { due_date: e.target.value })}
                data-testid={`r11-task-due-${t.id}`} style={{ fontSize: 11, padding: 3 }} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function MoistureTab({ jobId }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => j(`/api/iv/r11/moisture/${jobId}`).then(setData).catch(() => setData({ maps: [], zone_colors: {} })), [jobId]);
  useEffect(() => { load(); }, [load]);
  const seedDemo = async () => {
    setBusy(true);
    try { await j(`/api/iv/r11/moisture/_seed-demo/${jobId}`, { method: "POST" }); load(); }
    finally { setBusy(false); }
  };
  if (!data) return <div>Loading…</div>;
  const colors = data.zone_colors || {};
  if (!data.maps.length) return (
    <div style={card} data-testid="r11-moisture-empty">
      <div style={subhead}>No moisture maps</div>
      <div style={{ fontSize: 12, color: "#475569", marginBottom: 10 }}>
        Moisture maps are read in from FieldProIQ. None are linked to this job yet.
      </div>
      <button data-testid="r11-moisture-seed-demo" onClick={seedDemo} disabled={busy} style={btnGhost}>
        Seed demo moisture data
      </button>
    </div>
  );
  return (
    <div data-testid="r11-moisture-tab">
      {data.maps.map(m => (
        <React.Fragment key={m.id}>
          {(m.rooms || []).map((room, ri) => (
            <div key={ri} style={card}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={subhead}>{room.name}</div>
                <div style={{ fontSize: 11, color: "#475569" }}>Cause: {room.cause}</div>
              </div>
              <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse", marginTop: 6 }}>
                <thead>
                  <tr style={{ background: "#f8fafc" }}>
                    <th style={{ padding: 6, textAlign: "left" }}>Point</th>
                    <th style={{ padding: 6, textAlign: "left" }}>Material</th>
                    <th style={{ padding: 6, textAlign: "right" }}>Moisture %</th>
                    <th style={{ padding: 6, textAlign: "center" }}>Zone</th>
                  </tr>
                </thead>
                <tbody>
                  {(room.readings || []).map((r, i) => (
                    <tr key={i} data-testid={`r11-reading-${m.id}-${ri}-${i}`}>
                      <td style={{ padding: 6, fontFamily: "monospace" }}>{r.point}</td>
                      <td style={{ padding: 6 }}>{r.material}</td>
                      <td style={{ padding: 6, textAlign: "right", fontWeight: 700 }}>{r.moisture_pct}%</td>
                      <td style={{ padding: 6, textAlign: "center" }}>
                        <span style={{ ...pill(colors[r.zone] || "#6b7280"), textTransform: "uppercase" }}>{r.zone}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
          {m.ai_analysis && (
            <div style={{ ...card, background: "#1e293b", color: "#e2e8f0" }} data-testid="r11-ai-analysis">
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <div style={{ ...subhead, color: "#94a3b8" }}>AI ANALYSIS</div>
                <div>
                  <span style={pill("#dc2626")}>{m.ai_analysis.severity}</span>{" "}
                  <span style={pill("#1e40af")}>{m.ai_analysis.iicrc_class}</span>{" "}
                  <span style={pill("#475569")}>{m.ai_analysis.iicrc_category}</span>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 8, fontSize: 12 }}>
                <div><strong>Drying days estimate:</strong> {m.ai_analysis.drying_days_estimate}</div>
                <div><strong>Mold risk:</strong> {m.ai_analysis.mold_risk}</div>
                <div><strong>Health risk:</strong> {m.ai_analysis.health_risk}</div>
              </div>
              <div style={{ marginTop: 10, fontSize: 12 }}>
                <div style={{ fontWeight: 700, color: "#fbbf24" }}>Immediate Actions:</div>
                <ul style={{ margin: "4px 0 0 18px", padding: 0 }}>
                  {(m.ai_analysis.immediate_actions || []).map((a, i) => <li key={i}>{a}</li>)}
                </ul>
              </div>
              <div style={{ marginTop: 10, fontSize: 12 }}>
                <div style={{ fontWeight: 700, color: "#fbbf24" }}>Equipment Recommended:</div>
                <ul style={{ margin: "4px 0 0 18px", padding: 0 }}>
                  {(m.ai_analysis.equipment_recommended || []).map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </div>
              <div style={{ marginTop: 10, fontSize: 12 }}>
                <div style={{ fontWeight: 700, color: "#fbbf24" }}>Affected Materials → Drying Goals:</div>
                <table style={{ width: "100%", fontSize: 11, marginTop: 4 }}>
                  <thead><tr style={{ color: "#94a3b8" }}><th align="left">Material</th><th>Current</th><th>Goal</th><th>Action</th></tr></thead>
                  <tbody>
                  {(m.ai_analysis.affected_materials || []).map((a, i) => (
                    <tr key={i}><td>{a.material}</td><td align="center">{a.current_pct}%</td><td align="center">{a.goal_pct}%</td><td align="center">{a.action}</td></tr>
                  ))}
                  </tbody>
                </table>
              </div>
              <div style={{ marginTop: 10, fontSize: 11, color: "#475569" }}>
                <strong>Insurance notes:</strong> {m.ai_analysis.insurance_notes}
              </div>
              <button data-testid="r11-moisture-pdf" onClick={() => window.print()} style={{ ...btnAlt, marginTop: 12, fontSize: 11 }}>
                Drying Report (PDF — print)
              </button>
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ─── PhotosTabR14 (Round 14 FIX 3 / R15 FIX 2) — categorized photo manager with per-section upload ──
function PhotosTabR14({ jobId }) {
  const [activeCat, setActiveCat] = useState("before");
  const [activeSub, setActiveSub] = useState({});  // { [catKey]: subLabel }
  const [photos, setPhotos] = useState([]);
  const [pairing, setPairing] = useState(null);     // { beforeId, afterId }
  const cats = [
    { key: "before",   label: "BEFORE",            subs: ["Initial damage documentation", "Moisture readings shown", "Safety hazards", "Overall property exterior"] },
    { key: "during",   label: "DURING MITIGATION", subs: ["Equipment placement", "Daily progress", "Containment barriers", "Moisture readings in progress"] },
    { key: "after",    label: "AFTER COMPLETION",  subs: ["Final condition all rooms", "Equipment removed confirmation", "Restored areas", "Before/After comparisons"] },
  ];
  // Persist photos in localStorage keyed by job — additive, no backend write
  const STORAGE = `r15_photos_${jobId}`;
  useEffect(() => { try { setPhotos(JSON.parse(localStorage.getItem(STORAGE) || "[]")); } catch { setPhotos([]); } }, [STORAGE]);
  const saveAll = (next) => { setPhotos(next); try { localStorage.setItem(STORAGE, JSON.stringify(next)); } catch {} };

  const uploadFiles = async (catKey, subLabel, files) => {
    const arr = [...files].slice(0, 12);
    const out = [];
    for (const f of arr) {
      const b64 = await new Promise((res, rej) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.onerror = rej; fr.readAsDataURL(f); });
      out.push({ id: "p-" + Date.now() + "-" + Math.random().toString(36).slice(2, 6), category: catKey, sub_category: subLabel || "", filename: f.name, data_uri: b64, uploaded_at: new Date().toISOString() });
    }
    saveAll([...photos, ...out]);
    toast(`Uploaded ${out.length} photo${out.length === 1 ? "" : "s"} to ${cats.find(c => c.key === catKey).label}`);
  };
  const movePhoto = (id, newCat) => saveAll(photos.map(p => p.id === id ? { ...p, category: newCat } : p));
  const delPhoto = (id) => saveAll(photos.filter(p => p.id !== id));

  const pairBefore = photos.find(p => p.id === pairing?.beforeId);
  const pairAfter = photos.find(p => p.id === pairing?.afterId);

  return (
    <div data-testid="r14-photos-tab" style={{ color: "#1a1a1a" }}>
      <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
        {cats.map(c => (
          <button key={c.key} onClick={() => setActiveCat(c.key)} data-testid={`r14-photo-cat-${c.key}`}
            style={{ flex: 1, padding: "8px 12px", fontSize: 11, fontWeight: 700, letterSpacing: 1,
                     border: "1px solid #d1d5db", borderRadius: 4, cursor: "pointer",
                     background: activeCat === c.key ? "#1e40af" : "#fff",
                     color: activeCat === c.key ? "#fff" : "#1a1a1a" }}>{c.label} ({photos.filter(p => p.category === c.key).length})</button>
        ))}
      </div>
      {cats.map(cat => {
        if (cat.key !== activeCat) return null;
        const catPhotos = photos.filter(p => p.category === cat.key);
        const sub = activeSub[cat.key] || cat.subs[0];
        return (
          <div key={cat.key} style={{ padding: "12px 14px", background: "#fafafa", border: "1px solid #e5e7eb", borderRadius: 6, marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#1a1a1a", letterSpacing: 1 }}>{cat.label}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <select value={sub} onChange={e => setActiveSub({ ...activeSub, [cat.key]: e.target.value })} data-testid={`r14-photo-sub-${cat.key}`} style={_inDark}>
                  {cat.subs.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <label className="btn btn-primary" style={{ fontSize: 11, cursor: "pointer", display: "inline-flex", alignItems: "center" }} data-testid={`r14-photo-upload-${cat.key}`}>
                  + Upload
                  <input type="file" accept="image/*" multiple style={{ display: "none" }} onChange={e => { uploadFiles(cat.key, sub, e.target.files); e.target.value = ""; }} />
                </label>
              </div>
            </div>
            <div style={{ fontSize: 10, color: "#666", fontWeight: 600, marginTop: 6 }}>
              Each upload below tags photos with category <strong>{cat.label}</strong> and sub-category <strong>{sub}</strong>. Category labels carry through to the PDF photo report.
            </div>
            <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
              {catPhotos.length === 0 && <div style={{ fontSize: 11, color: "#666", fontWeight: 600 }}>No photos in this category yet.</div>}
              {catPhotos.map(p => (
                <div key={p.id} data-testid={`r14-photo-item-${p.id}`} style={{ width: 130, padding: 6, background: "#fff", border: "1px solid #d1d5db", borderRadius: 6, display: "flex", flexDirection: "column", gap: 4 }}>
                  <img src={p.data_uri} alt={p.filename} style={{ width: "100%", height: 90, objectFit: "cover", borderRadius: 4 }} />
                  <div style={{ fontSize: 9, color: "#1a1a1a", fontWeight: 700, wordBreak: "break-all" }}>{p.filename}</div>
                  <div style={{ fontSize: 9, color: "#555", fontWeight: 600 }}>{p.sub_category}</div>
                  <div style={{ display: "flex", gap: 4 }}>
                    <select value={p.category} onChange={e => movePhoto(p.id, e.target.value)} style={{ ..._inDark, padding: "2px 4px", fontSize: 9 }} title="Move to category">
                      {cats.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                    </select>
                    <button onClick={() => delPhoto(p.id)} style={{ ..._delBtn, fontSize: 9 }} title="Delete">×</button>
                  </div>
                  {cat.key === "before" && <button onClick={() => setPairing({ beforeId: p.id, afterId: pairing?.afterId })} style={{ fontSize: 9, padding: "2px 4px", background: "#1e40af", color: "#fff", border: "none", borderRadius: 3, cursor: "pointer", fontWeight: 600 }}>Use as Before</button>}
                  {cat.key === "after" && <button onClick={() => setPairing({ beforeId: pairing?.beforeId, afterId: p.id })} style={{ fontSize: 9, padding: "2px 4px", background: "#1e40af", color: "#fff", border: "none", borderRadius: 3, cursor: "pointer", fontWeight: 600 }}>Use as After</button>}
                </div>
              ))}
            </div>
          </div>
        );
      })}
      {/* Before / After comparison */}
      <div style={{ padding: "10px 12px", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 6, marginTop: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#1a1a1a" }}>Before / After Comparison Pair</div>
        {pairBefore && pairAfter ? (
          <div data-testid="r14-photo-pair" style={{ marginTop: 8, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div><div style={{ fontSize: 10, fontWeight: 700, color: "#1a1a1a", marginBottom: 4 }}>BEFORE</div><img src={pairBefore.data_uri} alt="before" style={{ width: "100%", maxHeight: 180, objectFit: "cover", borderRadius: 4 }} /></div>
            <div><div style={{ fontSize: 10, fontWeight: 700, color: "#1a1a1a", marginBottom: 4 }}>AFTER</div><img src={pairAfter.data_uri} alt="after" style={{ width: "100%", maxHeight: 180, objectFit: "cover", borderRadius: 4 }} /></div>
            <div style={{ gridColumn: "1/-1", fontSize: 10, color: "#666", fontWeight: 600 }}>Both photos will appear side-by-side in the photo report PDF labelled "Before" and "After".</div>
          </div>
        ) : (
          <div style={{ fontSize: 11, color: "#1a1a1a", fontWeight: 500, marginTop: 4 }}>Pick one BEFORE photo (click "Use as Before") and one AFTER photo (click "Use as After") above to create a side-by-side comparison.</div>
        )}
      </div>
    </div>
  );
}

// ─── PhotosGateTab (legacy — kept untouched for back-compat) ──────────────
function PhotosGateTab({ jobId, photoCategory }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => j(`/api/iv/r11/photo-gate/${jobId}`).then(setData).catch(() => setData({ photo_count: 0, by_stage: {}, categories: [] })), [jobId]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div>Loading…</div>;
  return (
    <div data-testid="r11-photos-tab">
      <div style={card}>
        <div style={subhead}>Photo Gate Status · {data.photo_count} photos on this job</div>
        {Object.entries(data.by_stage).map(([stage, info]) => (
          <div key={stage} data-testid={`r11-photo-stage-${stage}`}
            style={{ background: info.ok ? "#dcfce7" : "#fee2e2", padding: 10, borderRadius: 4, marginBottom: 6 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 600 }}>
              <span>{stage.replace(/_/g, " ").toUpperCase()}</span>
              <span style={pill(info.ok ? "#16a34a" : "#dc2626")}>{info.ok ? "OK" : `${info.missing.length} MISSING`}</span>
            </div>
            {!info.ok && (
              <div style={{ fontSize: 11, marginTop: 6 }}>
                <strong>Missing:</strong> {info.missing.join(", ")}
              </div>
            )}
          </div>
        ))}
      </div>
      <div style={card}>
        <div style={subhead}>Photo Categories Available</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {data.categories.map(c => <span key={c} style={pill("#1e40af")}>{c}</span>)}
        </div>
        <div style={{ fontSize: 11, color: "#475569", marginTop: 10 }}>
          Photos are uploaded via the existing Documents tab — tag them with a category from above so this gate can count them. Stage advances will show a soft warning if required photos are missing, but won't hard-block.
        </div>
      </div>
    </div>
  );
}

function InsuranceClaimTab({ jobId }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const load = useCallback(() => j(`/api/iv/r11/claim/${jobId}`).then(d => { setData(d); setForm(d.claim || { job_id: jobId, supplements: [] }); }).catch(() => { setData({ claim: null }); setForm({ job_id: jobId, supplements: [] }); }), [jobId]);
  useEffect(() => { load(); }, [load]);
  const setF = (k, v) => setForm({ ...form, [k]: v });
  const save = async () => {
    await j(`/api/iv/r11/claim`, { method: "PUT", body: JSON.stringify({ ...form, job_id: jobId, supplements: form.supplements || [] }) });
    load();
  };
  const addSupp = () => setF("supplements", [...(form.supplements || []), { amount: 0, status: "submitted", note: "" }]);
  if (!data) return <div>Loading…</div>;
  const num = (k, lbl) => (
    <div><label style={{ fontSize: 10, color: "#475569" }}>{lbl}</label>
      <input type="number" step="0.01" value={form[k] || 0} onChange={e => setF(k, parseFloat(e.target.value) || 0)}
        data-testid={`r11-claim-${k}`} style={{ width: "100%", padding: 4, fontSize: 12 }} /></div>
  );
  const txt = (k, lbl) => (
    <div><label style={{ fontSize: 10, color: "#475569" }}>{lbl}</label>
      <input type="text" value={form[k] || ""} onChange={e => setF(k, e.target.value)}
        data-testid={`r11-claim-${k}`} style={{ width: "100%", padding: 4, fontSize: 12 }} /></div>
  );
  return (
    <div data-testid="r11-claim-tab">
      <div style={card}>
        <div style={subhead}>Carrier & Claim</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 8 }}>
          {txt("insurance_carrier", "Insurance Carrier")}
          {txt("claim_number", "Claim #")}
          {txt("policy_number", "Policy #")}
          <div><label style={{ fontSize: 10, color: "#475569" }}>Date of Loss</label>
            <input type="date" value={form.date_of_loss || ""} onChange={e => setF("date_of_loss", e.target.value)}
              data-testid="r11-claim-date_of_loss" style={{ width: "100%", padding: 4, fontSize: 12 }} /></div>
          {txt("adjuster_name", "Adjuster Name")}
          {txt("adjuster_phone", "Adjuster Phone")}
          {txt("adjuster_email", "Adjuster Email")}
          {txt("field_adjuster", "Field Adjuster")}
          <div><label style={{ fontSize: 10, color: "#475569" }}>Claim Status</label>
            <select value={form.claim_status || "New Claim"} onChange={e => setF("claim_status", e.target.value)}
              data-testid="r11-claim-status" style={{ width: "100%", padding: 4, fontSize: 12 }}>
              {data.statuses.map(s => <option key={s}>{s}</option>)}
            </select></div>
        </div>
      </div>
      <div style={card}>
        <div style={subhead}>Financials</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8 }}>
          {num("initial_estimate_amount", "Initial Estimate")}
          {num("carrier_approved_amount", "Carrier Approved")}
          {num("depreciation_amount", "Depreciation")}
          {num("recoverable_depreciation", "Recoverable Depr.")}
          {num("deductible_amount", "Deductible")}
          {num("acv_payment_amount", "ACV Payment")}
          {num("rcv_payment_amount", "RCV Payment")}
        </div>
        <div style={{ marginTop: 10, fontSize: 12 }}>Total approved: <strong>${(form.total_approved || 0).toLocaleString()}</strong></div>
      </div>
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div style={subhead}>Supplements</div>
          <button onClick={addSupp} style={btnGhost} data-testid="r11-claim-add-supp">+ Add</button>
        </div>
        {(form.supplements || []).map((s, i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr", gap: 6, marginBottom: 4 }}>
            <input type="number" placeholder="Amount" value={s.amount || 0}
              onChange={e => { const a = [...form.supplements]; a[i] = { ...a[i], amount: parseFloat(e.target.value) || 0 }; setF("supplements", a); }}
              style={{ padding: 4, fontSize: 12 }} />
            <select value={s.status} onChange={e => { const a = [...form.supplements]; a[i] = { ...a[i], status: e.target.value }; setF("supplements", a); }}
              style={{ padding: 4, fontSize: 12 }}>
              {["submitted", "approved", "denied"].map(o => <option key={o}>{o}</option>)}
            </select>
            <input type="text" placeholder="Note" value={s.note || ""}
              onChange={e => { const a = [...form.supplements]; a[i] = { ...a[i], note: e.target.value }; setF("supplements", a); }}
              style={{ padding: 4, fontSize: 12 }} />
          </div>
        ))}
      </div>
      <div style={card}>
        <div style={subhead}>TPA</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8 }}>
          {txt("tpa_company", "TPA Company")}
          {txt("tpa_contact", "TPA Contact")}
          {txt("tpa_reference", "TPA Reference")}
          {txt("tpa_program", "TPA Program")}
          <div><label style={{ fontSize: 10, color: "#475569" }}>TPA Status</label>
            <select value={form.tpa_status || "Not Submitted"} onChange={e => setF("tpa_status", e.target.value)}
              data-testid="r11-claim-tpa-status" style={{ width: "100%", padding: 4, fontSize: 12 }}>
              {["Not Submitted", "Submitted Pending", "Approved", "Rejected"].map(s => <option key={s}>{s}</option>)}
            </select></div>
        </div>
      </div>
      <button data-testid="r11-claim-save" onClick={save} style={btnAlt}>Save Claim</button>
    </div>
  );
}

function SignaturesTab({ jobId, job }) {
  const [list, setList] = useState(null);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ doc_type: "cos", customer_name: job?.customer || "", customer_phone: job?.phone || "", customer_email: job?.email || "", property_address: job?.address || "", scope_of_work: "", send_via: "both", expires_hours: 72 });
  const load = useCallback(() => j(`/api/iv/r11/sign/list?job_id=${jobId}`).then(setList).catch(() => setList({ count: 0, signatures: [] })), [jobId]);
  useEffect(() => { load(); }, [load]);
  const submit = async () => {
    try {
      const r = await j(`/api/iv/r11/sign/request`, { method: "POST", body: JSON.stringify({ ...form, job_id: jobId }) });
      alert(`Request created.\nLink: ${r.link}\nSMS: ${r.sms}  Email: ${r.email}`);
      setShow(false); load();
    } catch (e) { alert(e.message); }
  };
  const resend = async (sid) => { await j(`/api/iv/r11/sign/${sid}/resend`, { method: "POST" }); load(); };
  if (!list) return <div>Loading…</div>;
  return (
    <div data-testid="r11-sign-tab">
      <button data-testid="r11-sign-new" onClick={() => setShow(true)} style={btnAlt}>+ Request Signature</button>
      <div style={card}>
        <div style={subhead}>Signature Requests ({list.count})</div>
        {list.signatures.map(s => {
          const color = s.status === "signed" ? "#16a34a"
            : s.status === "awaiting_signature" ? "#eab308"
            : s.status === "expired" ? "#dc2626"
            : s.status === "declined" ? "#dc2626" : "#6b7280";
          return (
            <div key={s.id} data-testid={`r11-sign-row-${s.id}`} style={{ borderBottom: "1px solid #f1f5f9", padding: "8px 0", fontSize: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <strong>{s.doc_type_label}</strong>
                <span style={pill(color)}>{s.status.replace(/_/g, " ").toUpperCase()}</span>
              </div>
              <div style={{ color: "#475569" }}>Created: {(s.created_at || "").slice(0, 16).replace("T", " ")}{s.signed_at && ` · Signed: ${s.signed_at.slice(0, 16).replace("T", " ")} by ${s.signed_by}`}</div>
              {s.status === "awaiting_signature" && (
                <button onClick={() => resend(s.id)} style={{ ...btnGhost, fontSize: 10, padding: "2px 8px", marginTop: 4 }}
                  data-testid={`r11-sign-resend-${s.id}`}>Resend</button>
              )}
            </div>
          );
        })}
      </div>
      {show && (
        <div className="modal-overlay" onClick={() => setShow(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }} data-testid="r11-sign-modal">
            <div className="modal-header"><div style={{ fontFamily: "Rajdhani,sans-serif", fontSize: 16, fontWeight: 700 }}>Request Signature</div><button className="icon-btn" onClick={() => setShow(false)}>×</button></div>
            <div style={{ padding: 18 }}>
              <div className="form-group"><div className="form-lbl">Document Type</div>
                <select value={form.doc_type} onChange={e => setForm({ ...form, doc_type: e.target.value })} className="form-control" data-testid="r11-sign-doc-type" style={{ width: "100%" }}>
                  <option value="cos">Certificate of Satisfaction</option>
                  <option value="work_auth">Work Authorization</option>
                  <option value="lien_waiver_conditional">Conditional Lien Waiver</option>
                  <option value="lien_waiver_unconditional">Unconditional Lien Waiver</option>
                  <option value="change_order">Change Order Authorization</option>
                  <option value="indemnification">Indemnification Agreement</option>
                  <option value="authorized_worker">Authorized Worker Letter</option>
                  {/* R14 FIX 8 — additional document types */}
                  <option value="psychrometric_signoff">Psychrometric Log Sign-Off</option>
                  <option value="drying_completion_cert">Drying Completion Certificate</option>
                  <option value="repair_authorization">Repair Authorization</option>
                  <option value="mold_protocol">Mold Remediation Protocol Agreement</option>
                  <option value="contents_release">Contents Release Form</option>
                  <option value="property_access">Property Access Authorization</option>
                </select></div>
              <div className="form-group" style={{ marginTop: 8 }}><div className="form-lbl">Customer Name</div>
                <input value={form.customer_name} onChange={e => setForm({ ...form, customer_name: e.target.value })} className="form-control" data-testid="r11-sign-customer-name" style={{ width: "100%" }} /></div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8 }}>
                <div><div className="form-lbl">Phone</div><input value={form.customer_phone} onChange={e => setForm({ ...form, customer_phone: e.target.value })} className="form-control" data-testid="r11-sign-phone" style={{ width: "100%" }} /></div>
                <div><div className="form-lbl">Email</div><input value={form.customer_email} onChange={e => setForm({ ...form, customer_email: e.target.value })} className="form-control" data-testid="r11-sign-email" style={{ width: "100%" }} /></div>
              </div>
              <div className="form-group" style={{ marginTop: 8 }}><div className="form-lbl">Property Address</div>
                <input value={form.property_address} onChange={e => setForm({ ...form, property_address: e.target.value })} className="form-control" style={{ width: "100%" }} /></div>
              <div className="form-group" style={{ marginTop: 8 }}><div className="form-lbl">Scope of Work</div>
                <textarea rows={4} value={form.scope_of_work} onChange={e => setForm({ ...form, scope_of_work: e.target.value })} className="form-control" style={{ width: "100%" }} /></div>
              <div className="form-group" style={{ marginTop: 8 }}><div className="form-lbl">Send via</div>
                <select value={form.send_via} onChange={e => setForm({ ...form, send_via: e.target.value })} className="form-control" data-testid="r11-sign-send-via" style={{ width: "100%" }}>
                  <option value="both">SMS + Email</option>
                  <option value="sms">SMS only</option>
                  <option value="email">Email only</option>
                  <option value="link">Shareable link only</option>
                </select></div>
              <button onClick={submit} style={{ ...btn, marginTop: 12 }} data-testid="r11-sign-modal-submit">Send Signature Request</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CloseJobTab({ jobId, job, onClosed }) {
  const [pre, setPre] = useState(null);
  const [form, setForm] = useState({
    payment_status: "Outstanding", reason: "Job Complete Paid in Full", notes: "",
    total_invoiced: 0, total_collected: 0, outstanding_balance: 0,
    job_duration_days: 0, total_labor_hours: 0, total_materials_cost: 0,
    request_google_review: true,
    customer_name: job?.customer || "", customer_phone: job?.phone || "",
    customer_email: job?.email || "", property_address: job?.address || "",
  });
  const load = useCallback(() => j(`/api/iv/r11/close/preflight/${jobId}`).then(setPre).catch(() => setPre({ cos_signed: false, photos_ok: false, claim_complete: false, errors: [], warnings: [] })), [jobId]);
  useEffect(() => { load(); }, [load]);
  if (!pre) return <div>Loading…</div>;
  const setF = (k, v) => setForm({ ...form, [k]: v });
  const close = async () => {
    if (!window.confirm("Close this job? This will fire survey + thank-you to the customer.")) return;
    try {
      const r = await j(`/api/iv/r11/close/execute`, { method: "POST", body: JSON.stringify({ ...form, job_id: jobId }) });
      alert(`Job closed.\nSurvey link: ${r.survey?.link}\nThank-you SMS: ${r.sms}  Email: ${r.email}`);
      if (onClosed) onClosed();
    } catch (e) { alert(e.message); }
  };
  const check = (ok, lbl, action) => (
    <div data-testid={`r11-close-check-${lbl.replace(/\s+/g, "-").toLowerCase()}`}
      style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: ok ? "#dcfce7" : "#fee2e2", padding: 8, borderRadius: 4, marginBottom: 4, fontSize: 12 }}>
      <span>{ok ? "✓" : "✗"} {lbl}</span>{action}
    </div>
  );
  return (
    <div data-testid="r11-close-tab">
      <div style={card}>
        <div style={subhead}>Required Items</div>
        {check(pre.cos_signed, "Certificate of Satisfaction signed",
          !pre.cos_signed && <span style={{ fontSize: 10 }}>Go to Signatures tab</span>)}
        <div style={{ background: "#f8fafc", padding: 8, borderRadius: 4, marginBottom: 4 }}>
          <div style={{ fontSize: 12, marginBottom: 6 }}>Final payment status</div>
          <select value={form.payment_status} onChange={e => setF("payment_status", e.target.value)}
            data-testid="r11-close-payment" style={{ width: "100%", padding: 4, fontSize: 12 }}>
            {["Paid", "Partial", "Outstanding", "Write-Off"].map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
      </div>
      <div style={card}>
        <div style={subhead}>Recommended (not blocking)</div>
        {check(pre.compliance_complete >= pre.compliance_total && pre.compliance_total > 0,
          `Compliance tasks: ${pre.compliance_complete} of ${pre.compliance_total}`)}
        {check(pre.photos_ok_for_close, `Final photos uploaded${pre.photos_missing.length ? ` — missing: ${pre.photos_missing.join(", ")}` : ""}`)}
        {check(pre.moisture_maps_present, "Moisture maps present")}
      </div>
      <div style={card}>
        <div style={subhead}>Financials</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8 }}>
          <div><label style={{ fontSize: 10 }}>Total Invoiced</label><input type="number" value={form.total_invoiced} onChange={e => setF("total_invoiced", parseFloat(e.target.value) || 0)} style={{ width: "100%", padding: 4, fontSize: 12 }} data-testid="r11-close-invoiced" /></div>
          <div><label style={{ fontSize: 10 }}>Total Collected</label><input type="number" value={form.total_collected} onChange={e => setF("total_collected", parseFloat(e.target.value) || 0)} style={{ width: "100%", padding: 4, fontSize: 12 }} data-testid="r11-close-collected" /></div>
          <div><label style={{ fontSize: 10 }}>Outstanding</label><input type="number" value={form.outstanding_balance} onChange={e => setF("outstanding_balance", parseFloat(e.target.value) || 0)} style={{ width: "100%", padding: 4, fontSize: 12 }} data-testid="r11-close-outstanding" /></div>
          <div><label style={{ fontSize: 10 }}>Duration (days)</label><input type="number" value={form.job_duration_days} onChange={e => setF("job_duration_days", parseInt(e.target.value) || 0)} style={{ width: "100%", padding: 4, fontSize: 12 }} /></div>
          <div><label style={{ fontSize: 10 }}>Labor hours</label><input type="number" value={form.total_labor_hours} onChange={e => setF("total_labor_hours", parseFloat(e.target.value) || 0)} style={{ width: "100%", padding: 4, fontSize: 12 }} /></div>
          <div><label style={{ fontSize: 10 }}>Materials cost</label><input type="number" value={form.total_materials_cost} onChange={e => setF("total_materials_cost", parseFloat(e.target.value) || 0)} style={{ width: "100%", padding: 4, fontSize: 12 }} /></div>
        </div>
      </div>
      <div style={card}>
        <div style={subhead}>Reason for Closing *</div>
        <select value={form.reason} onChange={e => setF("reason", e.target.value)} data-testid="r11-close-reason" style={{ width: "100%", padding: 6, fontSize: 12 }}>
          {["Job Complete Paid in Full", "Job Complete Partial Payment", "Job Complete Insurance Direct Pay", "Cancelled by Customer", "Cancelled by Company", "Duplicate Job", "Other"].map(o => <option key={o}>{o}</option>)}
        </select>
        <textarea rows={2} placeholder="Notes (required if Other)" value={form.notes} onChange={e => setF("notes", e.target.value)}
          style={{ width: "100%", padding: 6, fontSize: 12, marginTop: 6 }} data-testid="r11-close-notes" />
      </div>
      <button onClick={close} style={{ ...btn, padding: "10px 16px", fontSize: 13 }} data-testid="r11-close-execute">
        Close Job & Send Thank-you / Survey
      </button>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// RESTORATION REPORTS PAGE (new in Reports menu)
// ════════════════════════════════════════════════════════════════════════════
const PIN_KEY = "apex_quick_reports_pinned";
function pinReport(key) {
  const arr = JSON.parse(localStorage.getItem(PIN_KEY) || "[]");
  if (!arr.includes(key)) { arr.push(key); localStorage.setItem(PIN_KEY, JSON.stringify(arr)); }
}
function unpinReport(key) {
  const arr = JSON.parse(localStorage.getItem(PIN_KEY) || "[]");
  localStorage.setItem(PIN_KEY, JSON.stringify(arr.filter(k => k !== key)));
}

export function PinPrintSendButtons({ reportKey, reportName }) {
  const [pinned, setPinned] = useState(() => (JSON.parse(localStorage.getItem(PIN_KEY) || "[]")).includes(reportKey));
  return (
    <div style={{ display: "flex", gap: 6 }}>
      <button data-testid={`r11-pin-${reportKey}`} onClick={() => { pinned ? unpinReport(reportKey) : pinReport(reportKey); setPinned(!pinned); }}
        style={{ ...btnGhost, fontSize: 10, padding: "3px 8px", color: pinned ? "#dc2626" : "#374151" }}
        title={pinned ? "Unpin from Quick Reports" : "Pin to Quick Reports"}>
        {pinned ? "★ Pinned" : "☆ Pin"}
      </button>
      <button data-testid={`r11-print-${reportKey}`} onClick={() => window.print()} style={{ ...btnGhost, fontSize: 10, padding: "3px 8px" }}>Print</button>
      <button data-testid={`r11-send-${reportKey}`} onClick={() => {
        const to = window.prompt("Send to email:", "");
        if (to) window.location.href = `mailto:${to}?subject=${encodeURIComponent(reportName)}&body=${encodeURIComponent("See attached report from Apex Infinity.")}`;
      }} style={{ ...btnGhost, fontSize: 10, padding: "3px 8px" }}>Send</button>
    </div>
  );
}

function ReportShell({ keyName, name, children }) {
  return (
    <div style={card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <div style={subhead}>{name}</div>
        <PinPrintSendButtons reportKey={keyName} reportName={name} />
      </div>
      {children}
    </div>
  );
}

export function RestorationReportsPage() {
  const [tab, setTab] = useState("job_production");
  const reports = [
    ["job_production", "Job Production Report"],
    ["daily_field", "Daily Field Report"],
    ["tech_performance", "Technician Performance"],
    ["ar_aging", "Accounts Receivable Aging"],
    ["carrier_performance", "Insurance Carrier Performance"],
    ["job_profitability", "Job Profitability"],
    ["review_conversion", "Google Review Conversion"],
  ];
  return (
    <div data-testid="r11-reports-page">
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 12 }}>
        {reports.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`r11-rep-tab-${k}`}
            style={{ ...btnGhost, fontSize: 11, background: tab === k ? "#1e40af" : "transparent", color: tab === k ? "#fff" : "#374151", borderColor: tab === k ? "#1e40af" : "#d1d5db" }}>
            {l}
          </button>
        ))}
      </div>
      {tab === "job_production" && <ReportJobProduction />}
      {tab === "daily_field" && <ReportDailyField />}
      {tab === "tech_performance" && <ReportTechPerformance />}
      {tab === "ar_aging" && <ReportARAging />}
      {tab === "carrier_performance" && <ReportCarrierPerf />}
      {tab === "job_profitability" && <ReportJobProfit />}
      {tab === "review_conversion" && <ReportReviewConv />}
    </div>
  );
}

const TableRows = ({ rows, cols, testid }) => (
  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }} data-testid={testid}>
    <thead><tr style={{ background: "#f8fafc" }}>{cols.map(c => <th key={c.key} style={{ padding: 6, textAlign: c.right ? "right" : "left" }}>{c.label}</th>)}</tr></thead>
    <tbody>
      {rows.length === 0 && <tr><td colSpan={cols.length} style={{ padding: 14, textAlign: "center", color: "#94a3b8" }}>No data yet.</td></tr>}
      {rows.map((r, i) => (
        <tr key={r.id || r.job_id || r.tech || r.carrier || i} style={{ borderBottom: "1px solid #f1f5f9" }}>
          {cols.map(c => <td key={c.key} style={{ padding: 6, textAlign: c.right ? "right" : "left" }}>{c.fmt ? c.fmt(r[c.key]) : (r[c.key] ?? "—")}</td>)}
        </tr>
      ))}
    </tbody>
  </table>
);

function ReportJobProduction() {
  const [d, setD] = useState(null);
  useEffect(() => { j("/api/iv/r11/reports/job_production").then(setD).catch(() => {}); }, []);
  return <ReportShell keyName="job_production" name="Job Production Report">
    {!d ? "Loading…" : <TableRows rows={d.active} testid="r11-rep-job-production-table"
      cols={[
        { key: "job_id", label: "Job #" }, { key: "customer", label: "Customer" }, { key: "address", label: "Address" },
        { key: "type", label: "Type" }, { key: "tech", label: "Tech" }, { key: "stage", label: "Stage" },
        { key: "days_in_production", label: "Days", right: true }, { key: "outstanding_tasks", label: "Open Tasks", right: true },
      ]} />}
  </ReportShell>;
}
function ReportDailyField() {
  const [d, setD] = useState(null);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  useEffect(() => { j(`/api/iv/r11/reports/daily_field?date=${date}`).then(setD).catch(() => {}); }, [date]);
  return <ReportShell keyName="daily_field" name="Daily Field Report">
    <input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="r11-rep-daily-date" style={{ marginBottom: 10, padding: 4, fontSize: 12 }} />
    {!d ? "Loading…" :
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 10 }}>
        {[["Techs on site", d.techs_on_site], ["Timecards", d.timecards], ["Hours logged", d.hours_logged],
          ["Photos uploaded", d.photos_uploaded], ["Materials consumed", d.materials_consumed]].map(([l, v]) => (
          <div key={l} style={{ background: "#f8fafc", padding: 10, borderRadius: 6 }}>
            <div style={{ fontSize: 10, color: "#475569" }}>{l.toUpperCase()}</div>
            <div style={{ fontSize: 22, fontWeight: 700, fontFamily: "Rajdhani,sans-serif" }}>{v}</div>
          </div>
        ))}
      </div>}
  </ReportShell>;
}
function ReportTechPerformance() {
  const [d, setD] = useState(null);
  const [days, setDays] = useState(30);
  useEffect(() => { j(`/api/iv/r11/reports/tech_performance?days=${days}`).then(setD).catch(() => {}); }, [days]);
  return <ReportShell keyName="tech_performance" name="Technician Performance">
    <select value={days} onChange={e => setDays(parseInt(e.target.value))} style={{ marginBottom: 10, padding: 4, fontSize: 12 }}>
      {[7, 14, 30, 60, 90].map(n => <option key={n} value={n}>Last {n} days</option>)}
    </select>
    {!d ? "Loading…" : <>
      <div style={{ fontSize: 11, color: "#475569", marginBottom: 6 }}>Company avg survey score: <strong>{d.company_avg_score ?? "—"}</strong></div>
      <TableRows rows={d.rows} testid="r11-rep-tech-table"
        cols={[{ key: "tech", label: "Tech" }, { key: "shifts", label: "Shifts", right: true },
               { key: "hours", label: "Hours", right: true }, { key: "avg_survey_score", label: "Avg Score", right: true }]} />
    </>}
  </ReportShell>;
}
function ReportARAging() {
  const [d, setD] = useState(null);
  useEffect(() => { j("/api/iv/r11/reports/ar_aging").then(setD).catch(() => {}); }, []);
  return <ReportShell keyName="ar_aging" name="Accounts Receivable Aging">
    {!d ? "Loading…" : <>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 14 }}>
        {Object.entries(d.buckets).map(([b, v]) => (
          <div key={b} style={{ background: b === "90+" ? "#fee2e2" : "#f8fafc", padding: 10, borderRadius: 6 }}>
            <div style={{ fontSize: 10, color: "#475569" }}>{b} DAYS</div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>${(v || 0).toLocaleString()}</div>
          </div>
        ))}
      </div>
      <TableRows rows={d.rows} testid="r11-rep-ar-table"
        cols={[{ key: "job_id", label: "Job" }, { key: "outstanding", label: "Outstanding", right: true, fmt: v => `$${(v || 0).toLocaleString()}` },
               { key: "age_days", label: "Age (d)", right: true }, { key: "bucket", label: "Bucket" }]} />
    </>}
  </ReportShell>;
}
function ReportCarrierPerf() {
  const [d, setD] = useState(null);
  useEffect(() => { j("/api/iv/r11/reports/carrier_performance").then(setD).catch(() => {}); }, []);
  return <ReportShell keyName="carrier_performance" name="Insurance Carrier Performance">
    {!d ? "Loading…" : <TableRows rows={d.rows} testid="r11-rep-carrier-table"
      cols={[{ key: "carrier", label: "Carrier" }, { key: "claim_count", label: "Claims", right: true },
             { key: "total_approved", label: "Approved $", right: true, fmt: v => `$${(v || 0).toLocaleString()}` },
             { key: "supplements_submitted", label: "Supps", right: true },
             { key: "supplement_approval_rate", label: "Supp Approval %", right: true, fmt: v => v == null ? "—" : `${v}%` }]} />}
  </ReportShell>;
}
function ReportJobProfit() {
  const [d, setD] = useState(null);
  useEffect(() => { j("/api/iv/r11/reports/job_profitability").then(setD).catch(() => {}); }, []);
  return <ReportShell keyName="job_profitability" name="Job Profitability">
    {!d ? "Loading…" : <TableRows rows={d.rows} testid="r11-rep-profit-table"
      cols={[{ key: "job_id", label: "Job" }, { key: "invoiced", label: "Invoiced", right: true, fmt: v => `$${(v || 0).toLocaleString()}` },
             { key: "collected", label: "Collected", right: true, fmt: v => `$${(v || 0).toLocaleString()}` },
             { key: "labor_cost", label: "Labor", right: true, fmt: v => `$${(v || 0).toLocaleString()}` },
             { key: "materials_cost", label: "Materials", right: true, fmt: v => `$${(v || 0).toLocaleString()}` },
             { key: "gross_profit", label: "Gross", right: true, fmt: v => `$${(v || 0).toLocaleString()}` },
             { key: "margin_pct", label: "Margin %", right: true, fmt: v => v == null ? "—" : `${v}%` }]} />}
  </ReportShell>;
}
function ReportReviewConv() {
  const [d, setD] = useState(null);
  useEffect(() => { j("/api/iv/r11/reports/review_conversion").then(setD).catch(() => {}); }, []);
  return <ReportShell keyName="review_conversion" name="Google Review Conversion">
    {!d ? "Loading…" :
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 10 }}>
        {[["Jobs Closed", d.jobs_closed], ["Surveys Sent", d.surveys_sent], ["Completed", d.surveys_completed],
          ["Conversion %", `${d.conversion_pct}%`], ["Google prompts", d.google_review_prompts],
          ["Promoters", d.promoters], ["Detractors", d.detractors], ["Avg score", d.avg_star_rating_estimate ?? "—"]].map(([l, v]) => (
          <div key={l} style={{ background: "#f8fafc", padding: 10, borderRadius: 6 }}>
            <div style={{ fontSize: 10, color: "#475569" }}>{l.toUpperCase()}</div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{v}</div>
          </div>
        ))}
      </div>}
  </ReportShell>;
}

// ════════════════════════════════════════════════════════════════════════════
// SCHEDULE ALL VIEWS — month/week/tech/emp/emp-week + existing day toggle
// ════════════════════════════════════════════════════════════════════════════
export function ScheduleAllViewsPage() {
  const [view, setView] = useState("month");
  const [d, setD] = useState(new Date());
  const views = [
    ["month", "Month"], ["week", "Week"], ["day", "Day"],
    ["tech", "Tech"], ["emp", "Employee"], ["emp-week", "Emp Week"],
    ["oncall", "On-Call"], ["wo", "WO View"],
  ];
  return (
    <div data-testid="r11-schedule-views">
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            {views.map(([k, l]) => (
              <button key={k} onClick={() => setView(k)} data-testid={`r11-sched-view-${k}`}
                style={{ ...btnGhost, fontSize: 11, background: view === k ? "#1e40af" : "transparent", color: view === k ? "#fff" : "#374151", borderColor: view === k ? "#1e40af" : "#d1d5db" }}>
                {l}
              </button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <button onClick={() => setD(new Date(d.getTime() - 86400000 * (view === "month" ? 30 : view === "week" ? 7 : 1)))} style={btnGhost}>‹</button>
            <span style={{ fontFamily: "monospace", fontSize: 12 }} data-testid="r11-sched-date">{d.toISOString().slice(0, 10)}</span>
            <button onClick={() => setD(new Date(d.getTime() + 86400000 * (view === "month" ? 30 : view === "week" ? 7 : 1)))} style={btnGhost}>›</button>
            <button onClick={() => setD(new Date())} style={btnGhost} data-testid="r11-sched-today">Today</button>
          </div>
        </div>
      </div>
      {view === "month" && <SchedMonth date={d} onPickDay={(dt) => { setD(dt); setView("day"); }} />}
      {view === "week" && <SchedWeek date={d} />}
      {view === "day" && <SchedDayPlaceholder date={d} />}
      {view === "tech" && <SchedTechGrid date={d} />}
      {view === "emp" && <SchedEmpGrid date={d} />}
      {view === "emp-week" && <SchedEmpWeek date={d} />}
      {view === "oncall" && <SchedOnCall />}
      {view === "wo" && <SchedWO />}
    </div>
  );
}

function SchedMonth({ date, onPickDay }) {
  const y = date.getFullYear(), m = date.getMonth();
  const first = new Date(y, m, 1), last = new Date(y, m + 1, 0);
  const startDow = first.getDay();
  const days = [];
  for (let i = 0; i < startDow; i++) days.push(null);
  for (let i = 1; i <= last.getDate(); i++) days.push(new Date(y, m, i));
  return (
    <div style={card} data-testid="r11-sched-month">
      <div style={{ fontFamily: "Rajdhani,sans-serif", fontSize: 16, fontWeight: 700, marginBottom: 8 }}>
        {first.toLocaleString("en-US", { month: "long", year: "numeric" })}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 2 }}>
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => <div key={d} style={{ textAlign: "center", fontSize: 10, color: "#475569", padding: 4 }}>{d}</div>)}
        {days.map((d, i) => d ? (
          <button key={i} onClick={() => onPickDay && onPickDay(d)} data-testid={`r11-sched-month-day-${d.getDate()}`}
            style={{ padding: 10, background: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: 4, minHeight: 60, textAlign: "left", cursor: "pointer", fontSize: 12 }}>
            {d.getDate()}
          </button>
        ) : <div key={i} />)}
      </div>
    </div>
  );
}
function SchedWeek({ date }) {
  const start = new Date(date); start.setDate(start.getDate() - start.getDay());
  return (
    <div style={card} data-testid="r11-sched-week">
      <div style={{ display: "grid", gridTemplateColumns: "60px repeat(7,1fr)", gap: 2 }}>
        <div></div>
        {Array.from({ length: 7 }, (_, i) => { const d = new Date(start); d.setDate(d.getDate() + i); return (
          <div key={i} style={{ textAlign: "center", padding: 6, fontWeight: 600, background: "#f8fafc", fontSize: 12 }}>{d.toLocaleString("en-US", { weekday: "short", day: "numeric" })}</div>
        ); })}
        {[8, 9, 10, 11, 12, 13, 14, 15, 16, 17].map(h => (
          <React.Fragment key={h}>
            <div style={{ fontSize: 10, color: "#475569", padding: 4 }}>{h}:00</div>
            {Array.from({ length: 7 }, (_, i) => <div key={i} style={{ minHeight: 36, background: "#f8fafc", border: "1px solid #e5e7eb" }} />)}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
function SchedDayPlaceholder({ date }) {
  return <div style={card} data-testid="r11-sched-day-pl">
    <div style={subhead}>Day view · {date.toLocaleDateString()}</div>
    <div style={{ fontSize: 12, color: "#475569" }}>Open <strong>Schedule Day View</strong> from the Operations menu for the full interactive day timeline (existing module).</div>
  </div>;
}
function SchedTechGrid({ date }) {
  const techs = ["T-Alpha", "T-Bravo", "T-Charlie", "T-Delta", "T-Echo"];
  const colors = ["#1e40af", "#16a34a", "#dc2626", "#f97316", "#7c3aed"];
  return (
    <div style={card} data-testid="r11-sched-tech">
      <div style={subhead}>Tech View · {date.toLocaleDateString()}</div>
      <div style={{ display: "grid", gridTemplateColumns: `60px repeat(${techs.length},1fr)`, gap: 2 }}>
        <div></div>
        {techs.map((t, i) => <div key={t} style={{ background: colors[i], color: "#fff", padding: 6, fontSize: 11, fontWeight: 600, textAlign: "center" }}>{t}</div>)}
        {[8, 9, 10, 11, 12, 13, 14, 15, 16, 17].map(h => (
          <React.Fragment key={h}>
            <div style={{ fontSize: 10, color: "#475569", padding: 4 }}>{h}:00</div>
            {techs.map((t, i) => (
              <div key={t} style={{ minHeight: 36, background: "#f8fafc", border: "1px solid #e5e7eb", position: "relative", padding: 2 }}>
                {h === 9 + i % 3 && (
                  <div data-testid={`r11-sched-tech-job-${t}-${h}`}
                    style={{ background: colors[i], color: "#fff", padding: 4, fontSize: 10, borderRadius: 3, cursor: "pointer" }}>
                    J-{2040 + i}{h}
                  </div>
                )}
              </div>
            ))}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
function SchedEmpGrid({ date }) { return <SchedTechGrid date={date} />; }
function SchedEmpWeek({ date }) { return <SchedWeek date={date} />; }
function SchedOnCall() {
  return <div style={card} data-testid="r11-sched-oncall">
    <div style={subhead}>On-Call Rotation</div>
    <table style={{ width: "100%", fontSize: 12 }}>
      <thead><tr style={{ background: "#f8fafc" }}><th align="left">Week</th><th>Primary</th><th>Backup</th></tr></thead>
      <tbody>{[["This week", "T-Alpha", "T-Bravo"], ["Next week", "T-Charlie", "T-Delta"], ["Following", "T-Echo", "T-Alpha"]].map((r, i) => (
        <tr key={i}><td style={{ padding: 4 }}>{r[0]}</td><td align="center">{r[1]}</td><td align="center">{r[2]}</td></tr>
      ))}</tbody>
    </table>
  </div>;
}
function SchedWO() {
  return <div style={card} data-testid="r11-sched-wo">
    <div style={subhead}>Work Order View</div>
    <div style={{ fontSize: 12, color: "#475569" }}>This is a placeholder view showing WO-aligned scheduling. Use the live <em>ARM Work Orders</em> module under Administration for the production sync.</div>
  </div>;
}

// ════════════════════════════════════════════════════════════════════════════
// MISC — Call/SMS helpers for map drawer, Team row extras
// ════════════════════════════════════════════════════════════════════════════
export function MapPinContactBar({ name, phone, profileUrl }) {
  if (!phone) return <div style={{ fontSize: 11, color: "#94a3b8" }}>No phone on file.</div>;
  return (
    <div data-testid="r11-pin-contact" style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
      <a href={`tel:${phone}`} data-testid="r11-pin-call" style={{ ...btnAlt, textDecoration: "none", fontSize: 11 }}>📞 Call</a>
      <a href={`sms:${phone}`} data-testid="r11-pin-sms" style={{ ...btn, textDecoration: "none", fontSize: 11 }}>💬 SMS</a>
      {profileUrl && <a href={profileUrl} data-testid="r11-pin-profile" style={{ ...btnGhost, textDecoration: "none", fontSize: 11 }}>View Full Profile</a>}
      <span style={{ fontSize: 11, color: "#475569", alignSelf: "center" }}>{name && `→ ${name}`}</span>
    </div>
  );
}

export function TeamRowExtras({ memberId, memberName, onResent, onActivity }) {
  const [busy, setBusy] = useState(false);
  const reset = async () => {
    if (!window.confirm(`Send password reset to ${memberName}?`)) return;
    setBusy(true);
    try {
      // Re-use existing invite-resend / reset flow (POST /api/iv/team/{id}/resend-invite)
      await fetch(`${API}/api/iv/team/${memberId}/resend-invite`, { method: "POST", headers: _h() });
      alert("Password reset / invite re-sent.");
      if (onResent) onResent();
    } catch (e) { alert("Failed: " + (e?.message || e)); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ display: "flex", gap: 4 }}>
      <button data-testid={`r11-team-reset-${memberId}`} onClick={reset} disabled={busy}
        style={{ ...btnGhost, fontSize: 10, padding: "2px 8px" }}>{busy ? "…" : "Reset PW"}</button>
      <button data-testid={`r11-team-activity-${memberId}`} onClick={() => onActivity && onActivity(memberId)}
        style={{ ...btnGhost, fontSize: 10, padding: "2px 8px" }}>Activity Log</button>
    </div>
  );
}
